/** What the team decided when it reviewed a feedback item. The server checks the value. */
export type ReviewDecision = "do_now" | "do_later" | "wont_do";

export interface ReviewDecisionMeta {
  key: ReviewDecision;
  /** In the "Mark reviewed" dialog. */
  label: string;
  /** On chips: "Reviewed · Do now". */
  short: string;
  hint: string;
  /** Tailwind classes for the chip. */
  chip: string;
}

export const REVIEW_DECISIONS: readonly ReviewDecisionMeta[] = [
  {
    key: "do_now",
    label: "Do it now",
    short: "Do now",
    hint: "Top of the queue — we'll work on this next.",
    chip: "text-fc-bad border-fc-bad/40 bg-fc-bad/10",
  },
  {
    key: "do_later",
    label: "Do it later",
    short: "Later",
    hint: "Worth doing, not urgent. Stays in the open queue.",
    chip: "text-fc-warn border-fc-warn/40 bg-fc-warn/10",
  },
  {
    key: "wont_do",
    label: "Not doing it",
    short: "Not doing",
    hint: "Closes it — leaves the open queue. Reopen any time.",
    chip: "text-fc-text-dim border-fc-border bg-fc-overlay",
  },
];

export function decisionMeta(key: string | null | undefined): ReviewDecisionMeta | null {
  return REVIEW_DECISIONS.find(d => d.key === key) ?? null;
}

const STATUS_NAMES: Record<string, string> = {
  new: "New",
  reviewed: "Reviewed",
  resolved: "Resolved",
};

/** "Reviewed · Do now". Other statuses show on their own. */
export function statusLabel(status: string, decision: string | null | undefined): string {
  const name = STATUS_NAMES[status] ?? status;
  if (status !== "reviewed") return name;
  return `${name} · ${decisionMeta(decision)?.short ?? "No decision"}`;
}

/** One row of filter chips on the list: statuses with the decisions in between. */
export type StatusFilter = "all" | "new" | ReviewDecision | "resolved";

export const STATUS_FILTERS: readonly { key: StatusFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  ...REVIEW_DECISIONS.map(d => ({ key: d.key, label: d.short })),
  { key: "resolved", label: "Resolved" },
];

/** The list API's status / decision query for a filter chip. */
export function statusFilterParams(f: StatusFilter): { status?: string; decision?: string } {
  if (f === "all") return {};
  if (f === "new" || f === "resolved") return { status: f };
  return { status: "reviewed", decision: f };
}
